package com.xorrus.trading.lot.relayclient.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Instant

/**
 * Titan — Positions-Abgleich als Fernsteuerung (Spec-Muster, mit Erweiterung).
 *
 * Anders als MMS/OpeningPut ist Titan kein „Schalter", sondern ein zweistufiger
 * Abgleich: ein **Vorschau-Read** (`strategy.preview`, §-Erweiterung, read-only)
 * rechnet TWS-IST gegen Folio-SOLL und liefert die vorgeschlagenen Aktionen; der
 * Nutzer wählt einzelne Aktionen an; `activate` führt genau die gewählten aus
 * (einziger mutierender Schritt). Kein laufender Zustand ⇒ kein echtes `deactivate`.
 */

val EQUITY_GROWTH_SYSTEM_STRATEGY_ID = StrategyId("equityGrowthSystem")
const val EQUITY_GROWTH_SYSTEM_SCHEMA_VERSION = 1

/**
 * Kapital des FolioButler-Musterdepots — Normierungsbasis des Wire-Kapitals
 * (Pendant zu EgsStrategy.MUSTERDEPOT_CAPITAL): [EgsParams.capital] =
 * Skalierungsfaktor × dieses Kapital. Das Drahtformat bleibt aus
 * Kompatibilitätsgründen kapitalbasiert; eingegeben und angezeigt wird der Faktor.
 */
const val EGS_MUSTERDEPOT_CAPITAL = 100_000.0

/**
 * Skalierungsfaktor (Eingabe-/Anzeigegröße) zum Wire-Kapital. [baseCapital] ist die
 * Anzeigebasis des Lizenz-Tiers ([EgsOptions.scaleBaseCapital]): Faktor 1 meint dieses
 * Kapital, nicht die Wire-Norm. Ohne Angabe bleibt es bei der Norm — der Default gilt
 * nur für Aufrufer ohne Options (altes Verhalten).
 */
fun egsScaleFactor(capital: Decimal, baseCapital: Double = EGS_MUSTERDEPOT_CAPITAL): Double =
    capital.toDouble() / baseCapital

/**
 * Wire-Kapital zum eingegebenen Skalierungsfaktor — auf ganze Kapitaleinheiten
 * gerundet (Faktor-Auflösung 1e-5 reicht für die Eingabe). [baseCapital] wie in
 * [egsScaleFactor].
 */
fun egsCapitalFromScaleFactor(factor: Double, baseCapital: Double = EGS_MUSTERDEPOT_CAPITAL): Decimal =
    Decimal(kotlin.math.round(factor * baseCapital).toLong().toString())

/**
 * Faktor als Anzeige-Text ohne überflüssige Nachkommastellen ("0.5", "1", "2.5") —
 * plattformneutral (JVM und JS formatieren Double.toString unterschiedlich).
 */
fun egsFormatScaleFactor(factor: Double): String {
    val rounded = kotlin.math.round(factor * 100) / 100
    val whole = rounded.toLong()
    return if (rounded == whole.toDouble()) whole.toString() else rounded.toString()
}

/** IST-Quelle des Abgleichs (Pendant zu EgsSource der Desktop-View). */
@Serializable
enum class EgsSource { TWS, DB_STRATEGY }

@Serializable
@SerialName("equityGrowthSystem")
data class EgsParams(
    val accountId: AccountId,
    /**
     * Reserviertes Kapital = Skalierungsfaktor × [EGS_MUSTERDEPOT_CAPITAL] — steuert die
     * Skalierung des SOLL. Eingegeben wird der Faktor; das Drahtformat bleibt kapitalbasiert.
     */
    val capital: Decimal,
    val transmitToExchange: Boolean,
    val source: EgsSource = EgsSource.TWS,
    /**
     * Für `activate`: die vom Nutzer bestätigten Aktions-IDs aus der Vorschau
     * (`EgsPreviewAction.id`). Bei `strategy.preview` ignoriert (leer lassen).
     */
    val selectedActionIds: List<String> = emptyList(),
    override val schemaVersion: Int = EQUITY_GROWTH_SYSTEM_SCHEMA_VERSION,
) : StrategyParams

@Serializable
@SerialName("equityGrowthSystem")
data class EgsOptions(
    val accounts: List<AccountRef>,
    /** zuletzt verwendeter Skalierungsfaktor × Musterdepot (geteilte Defaults) als Prefill-Vorschlag */
    val defaultCapital: Decimal? = null,
    /** Betreiber-Obergrenze für das Kapital (spiegelt [EgsBounds.maxCapital]); Formular-Hinweis (als Faktor angezeigt) */
    val maxCapital: Decimal? = null,
    /**
     * Musterdepot-Anzeige (reine Folio-Modelldaten der EGS-API, kontounabhängig und
     * unskaliert — Pendant zur Musterdepot-Tabelle der Desktop-View). `null` = alter
     * Desktop ohne dieses Feld ⇒ Abschnitt verbergen; Ladefehler stehen in
     * [musterdepotError] (Liste dann ebenfalls `null`).
     */
    val musterdepot: List<EgsMusterdepotRow>? = null,
    /**
     * Normierungsbasis der Musterdepot-Größen in USD (Lizenz-Tier-Basis) — nur gesetzt,
     * wenn das Folio im Prozent-Modus rechnet (Signal-Felder durchgängig); steuert den
     * Hinweistext unter der Tabelle.
     */
    val musterdepotBaseCapital: Decimal? = null,
    /** Ladefehler des Musterdepots (zweisprachig); der Rest des Formulars bleibt nutzbar. */
    val musterdepotError: RemoteText? = null,
    /**
     * Musterdepot nicht aktuell — mindestens ein Folio des Signal-Servers meldet `uptodate = false`
     * (Spiegel der Desktop-View, die dann „Ordervorschläge berechnen“ sperrt). `true` sperrt die
     * Vorschau; `false` (auch: alter Desktop ohne das Feld) lässt sie zu.
     */
    val musterdepotOutdated: Boolean = false,
    /**
     * Anzeigebasis des Skalierungsfaktors in USD (Lizenz-Tier-Basis, beim S-Tier 20.000):
     * Faktor 1 meint dieses Kapital — dieselbe Bedeutung wie in der Desktop-View. Das
     * Wire-Kapital bleibt davon unberührt auf der 100k-Norm ([EGS_MUSTERDEPOT_CAPITAL]).
     * `null` = alter Desktop ohne das Feld ⇒ Anzeige fällt auf die Norm zurück.
     */
    val scaleBaseCapital: Decimal? = null,
    /**
     * Quellen-Wahl des IST (TWS live vs. DB) freigeschaltet — Spiegel des Desktop-Features
     * FEATURE_TITAN_EGS_INSIDER (teamintern). `false` (auch: alter Desktop ohne das Feld):
     * das Formular blendet die Auswahl aus, und [EgsParams.source] muss [EgsSource.TWS]
     * sein — wie die Desktop-View, die ohne Feature immer gegen TWS live abgleicht.
     */
    val sourceSelectable: Boolean = false,
    override val asOf: Instant,
) : StrategyOptions

/**
 * Musterdepot-Zeile — fertige Anzeige-Strings aus Desktop-Formatierung (XBigNum-Herkunft),
 * wie die Zellen der Desktop-Tabelle: [position] unskalierte Stückzahl, [change]
 * vorzeichenbehaftete Änderung („+3"/„−2"); leer = keine Änderung.
 */
@Serializable
data class EgsMusterdepotRow(
    val symbol: String,
    val position: String,
    val change: String = "",
)

// ---- Vorschau (Read-Ergebnis von strategy.preview) --------------------------------

/** Eine einzelne auszuführende Aktion (Storno/Öffnen/Schließen) mit stabiler [id]. */
@Serializable
data class EgsPreviewAction(
    /** stabiler Schlüssel „<zeile>:<aktion>" — reist unverändert zurück in [EgsParams.selectedActionIds] */
    val id: String,
    val symbol: String,
    /** „Storno", „MKT", „LMT" */
    val kind: String,
    /** vorzeichenbehaftete Stückzahl bzw. Anzahl der Stornos (als Text, XBigNum-Herkunft) */
    val size: String,
    /** Limit-Anzeige („—", „@Open", „1.23") */
    val limit: String,
    /** Zeilen-Status (z. B. „aufstocken", „zu viel") */
    val statusLabel: String,
    /** hervorheben (auffälliger Zeilen-Status wie „zu viel"/„nicht geöffnet") */
    val highlight: Boolean = false,
    /** Zweisprachige Fassung von [statusLabel]; null bei alten Desktops. */
    val statusLabelText: RemoteText? = null,
)

@Serializable
data class EgsPreview(
    val actions: List<EgsPreviewAction> = emptyList(),
    /** Zusammenfassung, falls keine Aktion nötig ist bzw. für Diagnose */
    val message: String? = null,
    val asOf: Instant,
    /** Zweisprachige Fassung von [message]; null bei alten Desktops. */
    val messageText: RemoteText? = null,
)

// ---- Status-Projektion: One-Shot-Ausführung ---------------------------------------

@Serializable
enum class EgsResultState { PENDING, WORKING, FILLED, CANCELLED, REJECTED, NOT_TRANSMITTED, FAILED }

/** Rückmeldung je ausgeführter Order/Storno (Projektion von EgsOrderResult). */
@Serializable
data class EgsResult(
    val symbol: String,
    val state: EgsResultState,
    val detail: String? = null,
    /** Zweisprachige Fassung von [detail], sofern der Desktop den Text selbst formuliert; Broker-Rohtexte bleiben nur in [detail]. */
    val detailText: RemoteText? = null,
)

@Serializable
@SerialName("equityGrowthSystem")
data class EgsStatus(
    override val phase: StrategyPhase,
    override val activeParams: StrategyParams? = null,
    override val since: Instant? = null,
    override val message: String? = null,
    override val messageText: RemoteText? = null,
    val results: List<EgsResult> = emptyList(),
) : RemoteStrategyStatus {
    override val strategyId: StrategyId get() = EQUITY_GROWTH_SYSTEM_STRATEGY_ID
}

// ---- Bounds -------------------------------------------------------------------------

data class EgsBounds(
    val allowTransmitToExchange: Boolean = false,
    /** Obergrenze für das reservierte Kapital (Sanity gegen versehentliche Skalierung); null = frei */
    val maxCapital: Decimal? = null,
) : ParamsBounds<EgsParams> {

    override fun violations(params: EgsParams): List<String> = violationsText(params).map { it.de }

    override fun violationsText(params: EgsParams): List<RemoteText> {
        val violations = mutableListOf<RemoteText>()
        if (params.transmitToExchange && !allowTransmitToExchange) {
            violations += RemoteText(
                de = "transmitToExchange ist für Remote-Aktivierungen nicht freigegeben",
                en = "transmitToExchange is not enabled for remote activations",
            )
        }
        maxCapital?.let { max ->
            if (params.capital > max) {
                violations += RemoteText(
                    de = "Skalierungsfaktor ${egsFormatScaleFactor(egsScaleFactor(params.capital))} " +
                        "überschreitet das Limit ${egsFormatScaleFactor(egsScaleFactor(max))}",
                    en = "Scale factor ${egsFormatScaleFactor(egsScaleFactor(params.capital))} " +
                        "exceeds the limit ${egsFormatScaleFactor(egsScaleFactor(max))}",
                )
            }
        }
        return violations
    }
}

// ---- Geteilte Validierung (§3.2) ------------------------------------------------------

/** Meldung bei veraltetem Musterdepot ([EgsOptions.musterdepotOutdated]) — Formular-Hinweis wie Validierungsfehler. */
val EGS_MUSTERDEPOT_OUTDATED = RemoteText(
    de = "Das Musterdepot ist nicht aktuell (Signal veraltet) — Ordervorschläge sind gesperrt.",
    en = "The model portfolio is not up to date (signal outdated) — order suggestions are disabled.",
)

fun validateEquityGrowthSystem(params: EgsParams, options: EgsOptions?): List<ValidationError> {
    val errors = mutableListOf<ValidationError>()

    if (params.schemaVersion != EQUITY_GROWTH_SYSTEM_SCHEMA_VERSION) {
        errors += ValidationError(
            field = "schemaVersion",
            message = "Unbekannte schemaVersion ${params.schemaVersion} (unterstützt: $EQUITY_GROWTH_SYSTEM_SCHEMA_VERSION) — Controller-App aktualisieren",
            code = ValidationCode.UNSUPPORTED_SCHEMA,
            messageText = RemoteText(
                de = "Unbekannte schemaVersion ${params.schemaVersion} (unterstützt: $EQUITY_GROWTH_SYSTEM_SCHEMA_VERSION) — Controller-App aktualisieren",
                en = "Unknown schemaVersion ${params.schemaVersion} (supported: $EQUITY_GROWTH_SYSTEM_SCHEMA_VERSION) — update the controller app",
            ),
        )
    }
    if (params.capital.toDouble() <= 0.0) {
        errors += ValidationError(
            "capital",
            "Skalierungsfaktor muss größer 0 sein",
            messageText = RemoteText(
                de = "Skalierungsfaktor muss größer 0 sein",
                en = "Scale factor must be greater than 0",
            ),
        )
    }
    // Sanity-Obergrenze aus den Options (fängt Vertipper beim Faktor);
    // desktopseitig autoritativ, dieselbe Prüfung als Live-Hinweis im Formular.
    options?.maxCapital?.let { max ->
        if (params.capital > max) {
            // Der Deckel ist kapitalbasiert; angezeigt wird er als Faktor auf der Tier-Basis,
            // damit die Zahl zu der im Formular eingegebenen passt.
            val base = options.scaleBaseCapital?.toDouble() ?: EGS_MUSTERDEPOT_CAPITAL
            val maxFactor = egsFormatScaleFactor(egsScaleFactor(max, base))
            errors += ValidationError(
                "capital",
                "Skalierungsfaktor darf höchstens $maxFactor sein (Sanity-Grenze).",
                messageText = RemoteText(
                    de = "Skalierungsfaktor darf höchstens $maxFactor sein (Sanity-Grenze).",
                    en = "Scale factor must not exceed $maxFactor (sanity limit).",
                ),
            )
        }
    }
    if (options != null && options.accounts.none { it.id == params.accountId }) {
        errors += ValidationError(
            field = "accountId",
            message = "Das gewählte Konto ist nicht (mehr) verfügbar — Auswahl neu laden",
            code = ValidationCode.STALE_OPTION,
            messageText = RemoteText(
                de = "Das gewählte Konto ist nicht (mehr) verfügbar — Auswahl neu laden",
                en = "The selected account is not (or no longer) available — reload the selection",
            ),
        )
    }
    // Veraltetes Signal ⇒ keine Ordervorschläge (wie die Desktop-View). STALE_OPTION, damit ein
    // Controller mit noch „aktuellen“ Options die frischen nachlädt und den Button selbst sperrt.
    if (options != null && options.musterdepotOutdated) {
        errors += ValidationError(
            field = "",
            message = EGS_MUSTERDEPOT_OUTDATED.de,
            code = ValidationCode.STALE_OPTION,
            messageText = EGS_MUSTERDEPOT_OUTDATED,
        )
    }
    // Quellen-Wahl ist lizenzgebunden ([EgsOptions.sourceSelectable]); ohne Freigabe ist nur
    // TWS live zulässig — desktopseitig autoritativ, im Formular gar nicht erst wählbar.
    if (options != null && !options.sourceSelectable && params.source != EgsSource.TWS) {
        errors += ValidationError(
            field = "source",
            message = "IST-Quelle ist nicht freigeschaltet — nur TWS (live) zulässig",
            messageText = RemoteText(
                de = "IST-Quelle ist nicht freigeschaltet — nur TWS (live) zulässig",
                en = "Actual-position source is not enabled — only TWS (live) is allowed",
            ),
        )
    }
    return errors
}
